import React, { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  btnDefaultInstClass,
  btnGhostInstClass,
  btnLinkInstClass,
  btnOutlineInstClass,
  btnPrimaryInstClass,
  btnSecondaryInstClass,
} from '@/lib/institution-button';

const variantStyles = {
  default: btnDefaultInstClass,
  primary: btnPrimaryInstClass,
  secondary: btnSecondaryInstClass,
  outline: btnOutlineInstClass,
  ghost: btnGhostInstClass,
  destructive:
    'bg-[#D9534F] text-white hover:bg-[#B6403D] active:bg-[#8E312F] focus-visible:ring-[#D9534F] dark:bg-red-600 dark:hover:bg-red-700',
  link: cn(btnLinkInstClass, 'underline-offset-4 hover:underline p-0 h-auto font-medium'),
} as const;

const sizeStyles = {
  sm: 'min-h-8 h-8 px-3 text-xs gap-1.5 rounded-btn',
  md: 'min-h-10 h-10 px-4 text-sm gap-2 rounded-btn',
  lg: 'min-h-12 h-12 px-6 text-base gap-2.5 rounded-btn',
  icon: 'h-10 w-10 min-h-10 rounded-btn',
} as const;

export type ButtonVariant = keyof typeof variantStyles;
export type ButtonSize = keyof typeof sizeStyles;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      asChild = false,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const content = (
      <>
        {loading && (
          <Loader2
            className={cn('animate-spin', size === 'sm' ? 'h-3 w-3' : 'h-4 w-4')}
            aria-hidden="true"
          />
        )}
        {!loading && leftIcon && <span aria-hidden="true">{leftIcon}</span>}
        {children}
        {rightIcon && <span aria-hidden="true">{rightIcon}</span>}
      </>
    );

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={loading}
        className={cn(
          'inline-flex items-center justify-center font-semibold transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'dark:focus-visible:ring-offset-[hsl(217,33%,12%)]',
          'disabled:pointer-events-none disabled:opacity-50',
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';
